import React, {
    ForwardedRef,
    JSXElementConstructor,
    ReactElement,
    forwardRef,
    useCallback,
    useMemo,
    useRef,
} from 'react';

import { FlatList, ListRenderItem, ViewToken } from 'react-native';

import Carousel, { ICarouselInstance } from 'react-native-reanimated-carousel';
import { CarouselRenderItemInfo } from 'react-native-reanimated-carousel/lib/typescript/types';
import { useSelector } from 'react-redux';

import { selectPlayerConfig } from 'store/player/selectors';
import { MixedItem } from 'store/railsBuilder/reducer.types';

import { isTabletOrWeb, isWeb, screenWidth } from 'utils/responsiveness';

import {
    PHONE_GALLERY_HEIGHT,
    TABLET_GALLERY_HEIGHT,
} from 'components/Gallery/styles';

type Props = {
    items: MixedItem[];
    renderItem: (
        carouselItem: CarouselRenderItemInfo<MixedItem>,
    ) => ReactElement<any, string | JSXElementConstructor<any>>;
    screenReaderEnabled: boolean;
    autoPlay: boolean;
    isGalleryActive?: boolean;
    onProgressChange: (_: any, absoluteProgress: number) => void;
};

const GalleryCarousel = forwardRef(
    (
        {
            items,
            renderItem,
            screenReaderEnabled,
            autoPlay,
            isGalleryActive,
            onProgressChange,
        }: Props,
        ref: ForwardedRef<ICarouselInstance>,
    ) => {
        const galleryRef = useRef<FlatList>(null);
        const retryTimeoutRef = useRef<NodeJS.Timeout | number | null>(null);

        const { visible: playerVisible } = useSelector(selectPlayerConfig);
        const carouselData = useMemo(
            () => (isWeb && items.length === 2 ? items.concat(items) : items), // Workaround for web carousel with 2 items
            [items],
        );
        const viewabilityConfig = useMemo(
            () => ({
                itemVisiblePercentThreshold: 1,
                waitForInteraction: false,
            }),
            [],
        );
        const onConfigurePanGesture = useCallback((gestureChain) => {
            gestureChain.activeOffsetX([-10, 10]);
        }, []);

        const handleViewableItemsChanged = useCallback(
            ({ viewableItems }: { viewableItems: ViewToken[] }) => {
                const index = viewableItems[0]?.index || 0;

                onProgressChange(undefined, index);
            },
            [onProgressChange],
        );

        const handleScrollToIndexFailed = useCallback(
            ({ index }: { index: number }) => {
                if (retryTimeoutRef.current && !index) {
                    return;
                }

                retryTimeoutRef.current = setTimeout(() => {
                    galleryRef.current?.scrollToIndex({
                        index,
                        animated: false,
                    });

                    retryTimeoutRef.current = null;
                }, 100);
            },
            [],
        );

        if (screenReaderEnabled) {
            return (
                <FlatList
                    ref={galleryRef}
                    data={items}
                    renderItem={
                        renderItem as unknown as ListRenderItem<MixedItem>
                    }
                    bounces={false}
                    viewabilityConfig={viewabilityConfig}
                    onViewableItemsChanged={handleViewableItemsChanged}
                    onScrollToIndexFailed={handleScrollToIndexFailed}
                    getItemLayout={(_, index) => ({
                        length: screenWidth,
                        offset: screenWidth * index,
                        index,
                    })}
                    keyExtractor={({ id }, index) => `${id}-${index}`}
                    showsHorizontalScrollIndicator={false}
                    pagingEnabled
                    horizontal
                    nestedScrollEnabled
                />
            );
        }

        return (
            <Carousel
                loop
                snapEnabled={!screenReaderEnabled}
                autoPlay={
                    isWeb
                        ? isGalleryActive && !playerVisible
                        : autoPlay && !screenReaderEnabled
                }
                pagingEnabled
                data={carouselData}
                ref={ref}
                defaultIndex={0}
                autoPlayInterval={5000}
                width={screenWidth}
                height={
                    isTabletOrWeb ? TABLET_GALLERY_HEIGHT : PHONE_GALLERY_HEIGHT
                }
                renderItem={renderItem}
                onProgressChange={onProgressChange}
                enabled={autoPlay && !isWeb}
                onConfigurePanGesture={onConfigurePanGesture}
            />
        );
    },
);

export default GalleryCarousel;
